import { isEmptyArray, isEmptyValue, isObject } from '../../jsUtils';

type FilterConfig = {
  /** Filter empty values inside arrays. Default: false */
  filterArrayItems?: boolean;
};

/**
 * Filters an object by removing empty values and recursively processing nested objects.
 *
 * Filtering process:
 * 1. Removes fields with empty values (undefined, null, '', [], {})
 * 2. Recursively processes nested objects, applying the same filter
 * 3. Optionally filters array items when filterArrayItems: true
 * 4. Removes fields where nested objects became empty after filtering
 * 5. Returns undefined if all fields were removed
 *
 * @param obj - Object to filter
 * @returns Filtered object or undefined if all fields are empty
 *
 * @example
 * // Default behavior - arrays pass through unchanged
 * getFilteredTruthyFieldsObj({ name: 'test', arr: [{ city: '' }] })
 * // Returns: { name: 'test', arr: [{ city: '' }] }
 *
 * @example
 * // With filterArrayItems: true
 * getFilteredTruthyFieldsObj({ name: 'test', arr: [{ city: '' }] }, { filterArrayItems: true })
 * // Returns: { name: 'test' } (empty object in array removed, empty array removed)
 */
export const getFilteredTruthyFieldsObj = <TObj extends Record<any, any>>(
  obj: TObj,
  config: FilterConfig = {},
): Partial<TObj> | undefined => {
  const { filterArrayItems = false } = config;

  const filteredEntries = Object.entries(obj)
    .map(([field, value]) => {
      if (isEmptyValue(value)) {
        return [field, undefined];
      }

      if (Array.isArray(value) && filterArrayItems) {
        return [field, getFilteredTruthyFieldsArray(value, config)];
      }

      if (isObject(value)) {
        return [field, getFilteredTruthyFieldsObj(value, config)];
      }

      return [field, value];
    })
    .filter(([, value]) => value !== undefined);

  if (isEmptyArray(filteredEntries)) {
    return undefined;
  } else {
    return Object.fromEntries(filteredEntries);
  }
};

const getFilteredTruthyFieldsArray = <TItem>(
  array: TItem[],
  config: FilterConfig,
): Partial<TItem>[] | undefined => {
  const filteredArray = array
    .map((item) => {
      if (isEmptyValue(item)) {
        return undefined;
      }

      if (Array.isArray(item)) {
        return getFilteredTruthyFieldsArray(item, config);
      }

      if (isObject(item)) {
        return getFilteredTruthyFieldsObj(item, config);
      }

      return item;
    })
    .filter((item) => item !== undefined) as Partial<TItem>[];

  if (isEmptyArray(filteredArray)) {
    return undefined;
  } else {
    return filteredArray;
  }
};
